import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Grid, Card, CardContent, Button, Box, Alert, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/apiService';

// V2 handler types for detection (complete list from developer guide)
const V2_HANDLERS = [
  'prepare_documents',
  'bedrock_converse',
  'bedrock_converse_simple',
  'estimate_tokens',
  'merge_rows',
  'render_json_to_excel',
  'prepare_parallel',
  'transcribe',
  'passthrough_aggregate',
  'concatenate_text_files',
  'textract_extract',
  'title_app',
  'comprehend_analyze',
  's3_upload',
  's3_download'
];

// Check if workflow is V2
/**
 * Check if workflow is V2 format (mister-fusion-v2)
 * V2 workflows have: metadata + workflow array with steps containing {name, handler, params}
 */
const isV2Workflow = (workflow: any): boolean => {
  if (!workflow) return false;
  
  // Primary detection: workflow array with step structure
  if (Array.isArray(workflow.workflow) && workflow.workflow.length > 0) {
    const firstStep = workflow.workflow[0];
    if (firstStep && typeof firstStep.name === 'string' && typeof firstStep.handler === 'string') {
      return true;
    }
  }
  
  // Secondary detection: schema_version indicates V2
  if (workflow.metadata?.schema_version?.startsWith('2.0')) {
    return true;
  }
  
  // Explicit V2 marker from backend (source === 'v2')
  if (workflow.isV2 || workflow.version === 'V2') {
    return true;
  }
  
  return false;
};

export default function PracticePage() {
  const { practice } = useParams();
  const navigate = useNavigate();
  
  const { data: practiceData, isLoading } = useQuery({
    queryKey: ['practice-details', practice],
    queryFn: async () => {
      const practices = await apiService.getPracticeDetails('');
      return practices.find((p: any) => p.id === practice);
    },
    enabled: !!practice
  });

  const formatTitle = (str: string) => 
    str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  if (isLoading) {
    return <Typography>Loading practice details...</Typography>;
  }

  if (!practiceData) {
    return (
      <Alert severity="error">
        Practice not found: {practice}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {formatTitle(practiceData.name)} Workflows
      </Typography>
      
      <Grid container spacing={3}>
        {practiceData.workflows?.filter((workflow: any) =>
          (workflow.access?.published === true || workflow.published === true) &&
          workflow.access?.visibleInPracticePage !== false &&
          workflow.visibleInPracticePage !== false
        ).map((workflow: any) => (
          <Grid item xs={12} sm={6} md={4} key={workflow.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {workflow.ui?.displayName || formatTitle(workflow.name)}
                  </Typography>
                  {workflow.recipeSource === 'v2' ? (
                    <Chip
                      label="V2"
                      size="small"
                      color="primary"
                      sx={{ ml: 1, fontWeight: 'bold', fontSize: '0.7rem' }}
                    />
                  ) : workflow.recipeSource === 'v1' ? (
                    <Chip
                      label="V1"
                      size="small"
                      sx={{ ml: 1, fontWeight: 'bold', fontSize: '0.7rem', backgroundColor: '#e0e0e0' }}
                    />
                  ) : null}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {workflow.ui?.description || workflow.description}
                </Typography>
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={() => navigate(`/${practice}/${workflow.id}`)}
                >
                  Start Workflow
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {(!practiceData.workflows || practiceData.workflows.length === 0) && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No workflows available for this practice
        </Typography>
      )}
    </Box>
  );
}